import { Button, Col, DatePicker, Form, Input, Row, Select, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import '@features/Incentive/incentive.css';
import '../stores.css';
import { Constants } from 'common/constants';
import { StoresSelector } from '../store/StoresSelector';
import { StoresActions } from '../store/StoresSlice';
import {
  AssignBarcodesToStore,
  GetAllStore,
  GetBarcodesByStoreId,
  RemoveBarcodeFromStore,
} from '../business/StoreBusiness';
import { EmployeeGetAllAgent, EmployeeSelector } from '@features/Employee';
import { StoreBarcodeViewModel } from 'common/models/BarcodeTypes';
import { TableLocaleCustom } from '@components/Custom/CustomTable';
import { OperationStatus } from 'store/rootTypes';
import { PATHS } from '@routing/routes';

export const AssignBarcodes = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const storeState = useSelector(StoresSelector);
  const employeeAgentState = useSelector(EmployeeSelector);
  const [barcodeText, setBarcodeText] = useState('');
  const [barcodeError, setBarcodeError] = useState('');
  const [storeError, setStoreError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (storeState.Stores.length < 1) {
        await dispatch(GetAllStore());
      }
      if (!employeeAgentState.Employee || employeeAgentState.Employee.length === 0) {
        await dispatch(EmployeeGetAllAgent());
      }
    };
    fetchData();
  }, []);

  const handleStoreChange = async (value: string) => {
    setStoreError('');
    if (!value) {
      form.setFieldsValue({ DroppedBy: undefined, DropDate: undefined });
    }
    await dispatch(GetBarcodesByStoreId(value));
  };

  const parseBarcodes = (text: string): string[] => {
    const barcodes = text
      .split(/[,\n]/)
      .map((b) => b.trim())
      .filter((b) => b.length > 0);
    return Array.from(new Set(barcodes));
  };

  const onFinish = async (values: any) => {
    if (!values.StoreId) {
      setStoreError('Please select a specific store to assign barcodes.');
      return;
    }
    setBarcodeError('');
    const parsedBarcodes = parseBarcodes(values.Barcodes);
    const error: any = await dispatch(
      AssignBarcodesToStore({
        StoreId: values.StoreId,
        DroppedBy: values.DroppedBy,
        DropDate: values.DropDate.format('YYYY-MM-DD'),
        Barcodes: parsedBarcodes,
      })
    );
    if (error) {
      setBarcodeError(error);
      return;
    }
    setBarcodeText('');
    form.setFieldsValue({ Barcodes: '' });
    await dispatch(GetBarcodesByStoreId(values.StoreId));
  };

  const data: StoreBarcodeViewModel[] = storeState.StoreBarcodes || [];
  const storeNameOptions = Array.from(new Set(data.map((item) => item.StoreName).filter(Boolean))).map((name) => ({
    text: name,
    value: name,
  }));
  const droppedByOptions = Array.from(new Set(data.map((item) => item.DroppedBy).filter(Boolean))).map((name) => ({
    text: name,
    value: name,
  }));
  const onDeleteBarcode = async (barcodeId: string) => {
    dispatch(StoresActions.SetStoreBarcodes(data.filter((b) => b.Id !== barcodeId)));
    await dispatch(RemoveBarcodeFromStore(barcodeId));
  };

  const columns = [
    {
      title: 'Barcode No',
      dataIndex: 'BarcodeNo',
      key: 'BarcodeNo',
      sorter: (a: StoreBarcodeViewModel, b: StoreBarcodeViewModel) => a.BarcodeNo.localeCompare(b.BarcodeNo),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="assign-barcode-filter">
          <Input
            placeholder="Search barcode"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
          <div>
            <Button type="link" size="small" onClick={() => clearFilters && clearFilters()}>
              Reset
            </Button>
            <Button type="primary" size="small" onClick={() => confirm()} icon={<SearchOutlined />}>
              Search
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => <SearchOutlined className={filtered ? 'assign-barcode-filtered' : ''} />,
      onFilter: (value, record: StoreBarcodeViewModel) =>
        (record.BarcodeNo || '').toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: 'Store name',
      dataIndex: 'StoreName',
      key: 'StoreName',
      sorter: (a: StoreBarcodeViewModel, b: StoreBarcodeViewModel) => a.StoreName.localeCompare(b.StoreName),
      filters: storeNameOptions,
      onFilter: (value: string | number | boolean, record: StoreBarcodeViewModel) => {
        if (typeof value === 'string') {
          return record.StoreName?.includes(value) ?? false;
        }
        return false;
      },
    },
    {
      title: 'Dropped by',
      dataIndex: 'DroppedBy',
      key: 'DroppedBy',
      sorter: (a: StoreBarcodeViewModel, b: StoreBarcodeViewModel) =>
        (a.DroppedBy || '').localeCompare(b.DroppedBy || ''),
      filters: droppedByOptions,
      onFilter: (value: string | number | boolean, record: StoreBarcodeViewModel) => {
        if (typeof value === 'string') {
          return record.DroppedBy?.includes(value) ?? false;
        }
        return false;
      },
    },
    {
      title: 'Drop off date',
      dataIndex: 'DropDate',
      key: 'DropDate',
      sorter: (a: StoreBarcodeViewModel, b: StoreBarcodeViewModel) =>
        new Date(a.DropDate).getTime() - new Date(b.DropDate).getTime(),
    },
    {
      title: 'Ref no.',
      dataIndex: 'RefNo',
      key: 'RefNo',
      sorter: (a: StoreBarcodeViewModel, b: StoreBarcodeViewModel) => (a.RefNo || '').localeCompare(b.RefNo || ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="assign-barcode-filter">
          <Input
            placeholder="Search ref no."
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
          />
          <div>
            <Button type="link" size="small" onClick={() => clearFilters && clearFilters()}>
              Reset
            </Button>
            <Button type="primary" size="small" onClick={() => confirm()} icon={<SearchOutlined />}>
              Search
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => <SearchOutlined className={filtered ? 'assign-barcode-filtered' : ''} />,
      onFilter: (value, record: StoreBarcodeViewModel) =>
        (record.RefNo || '').toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: 'Action',
      key: 'Action',
      render: (_text, record: StoreBarcodeViewModel) =>
        !record.TransactionsId && (
          <Button type="link" danger onClick={() => onDeleteBarcode(record.Id)}>
            Delete
          </Button>
        ),
    },
  ];

  const parsedBarcodes = parseBarcodes(barcodeText);

  return (
    <div className="App">
      <Row justify="center" align="middle">
        <h1>Assign barcodes to store</h1>
      </Row>

      <Form name="assignBarcodeForm" form={form} onFinish={onFinish} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item label="Store" name="StoreId">
              <Select
                size="large"
                showSearch
                placeholder="Select a store"
                optionFilterProp="children"
                onChange={handleStoreChange}
              >
                <Select.Option key="" value="">
                  All
                </Select.Option>
                {storeState.Stores?.map((s) => (
                  <Select.Option key={s.Id} value={s.Id}>
                    {s.Code} {s.Name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {storeError && <p className="assign-barcode-error">{storeError}</p>}
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Dropped by"
              name="DroppedBy"
              rules={[{ required: true, message: Constants.REQUIRED_FIELD }]}
            >
              <Select size="large" showSearch placeholder="Select an agent" optionFilterProp="children">
                {employeeAgentState?.Employee?.map((emp) => (
                  <Select.Option key={emp.UserId} value={emp.UserId}>
                    {emp.FirstName} {emp.LastName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Drop off date"
              name="DropDate"
              rules={[{ required: true, message: Constants.REQUIRED_FIELD }]}
            >
              <DatePicker size="large" className="assign-barcode-select" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col xs={24}>
            <Form.Item label="Barcodes" name="Barcodes" rules={[{ required: true, message: Constants.REQUIRED_FIELD }]}>
              <Input.TextArea
                rows={6}
                placeholder={
                  'Enter barcodes separated by commas or new lines\ne.g. VIC858344S,VIC820326S\nor one per line'
                }
                value={barcodeText}
                onChange={(e) => {
                  setBarcodeText(e.target.value);
                  setBarcodeError('');
                }}
              />
            </Form.Item>
            {barcodeError && <p className="assign-barcode-error">{barcodeError}</p>}
            {parsedBarcodes.length > 0 && (
              <p className="assign-barcode-count">{parsedBarcodes.length} barcode(s) detected</p>
            )}

            <Button
              className="prevSenderBtn"
              size="large"
              type="default"
              onClick={() => {
                dispatch(StoresActions.SetStoreBarcodes([]));
                history.push(PATHS.StoresIncentive.path);
              }}
            >
              Back
            </Button>
            <Button
              className="nextSenderBtn"
              size="large"
              type="primary"
              htmlType="submit"
              loading={storeState.submitStatus === OperationStatus.pending}
            >
              Assign Barcodes
            </Button>
          </Col>
        </Row>
      </Form>

      <br />
      <Table dataSource={data} columns={columns} locale={TableLocaleCustom} rowKey="Id" />
    </div>
  );
};
